// Official Pendo install script — typed, async, non-blocking.
// Injects pendo.js from cdn.pendo.io and primes the _q stub so callers can
// queue calls before the script finishes loading.

declare global {
  interface Window {
    pendo: PendoSDK;
  }
}

export interface PendoSDK {
  initialize: (options: PendoInitOptions) => void;
  clearSession: () => void;
  validateEnvironment?: () => void;
  _q?: unknown[];
  [key: string]: unknown;
}

export interface PendoVisitor {
  id: string;
}

export interface PendoAccount {
  id: string;
}

export interface PendoInitOptions {
  visitor: PendoVisitor;
  account: PendoAccount;
}

export function loadPendoScript(apiKey: string): void {
  // Prime the stub so calls queued before the script loads are replayed
  const pendo = (window.pendo || {}) as PendoSDK;
  if (!pendo._q) {
    pendo._q = [];
  }
  window.pendo = pendo;

  const methods = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];
  for (const method of methods) {
    if (!pendo[method]) {
      pendo[method] = (...args: unknown[]) => {
        pendo._q![method === 'initialize' ? 'unshift' : 'push']([method, ...args]);
      };
    }
  }

  // Inject the CDN script asynchronously
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript.parentNode?.insertBefore(script, firstScript);
}

export function waitForPendo(timeoutMs = 5000): Promise<void> {
  return new Promise((resolve, reject) => {
    const interval = 50;
    let elapsed = 0;
    const timer = setInterval(() => {
      if (typeof window.pendo?.initialize === 'function') {
        clearInterval(timer);
        resolve();
        return;
      }
      elapsed += interval;
      if (elapsed >= timeoutMs) {
        clearInterval(timer);
        reject(new Error('Pendo SDK did not load within timeout'));
      }
    }, interval);
  });
}
